import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  Typography,
  Link,
  Tooltip
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { get, map, find, round, isEmpty, includes, size } from 'lodash';
import HelpIcon from '@mui/icons-material/Help';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  tableContainer: {
    border: '1px solid #ced3d3',
    boxShadow: '0px 0px 1px 1px #cdd2d2'
  },
  '@keyframes pulsate': {
    '0%': {
      color: 'rgba(151,151,151, 0.2)'
    },
    '50%': {
      color: 'rgba(151,151,151, 1)'
    },
    '100%': {
      color: 'rgba(151,151,151, 0.2)'
    }
  },
  pulsating: {
    animation: '$pulsate 3s ease-in-out infinite'
  },
  oddRow: {
    // backgroundColor: theme.palette.background.paper,
    height: '2rem',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.hover}40`
    }
  },
  evenRow: {
    // backgroundColor: theme.palette.background.paper,
    height: '2rem',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.hover}40`
    }
  },
  tableHeader: {
    zIndex: 1
  },
  tableView: {
    borderRadius: '5px',
    overflow: 'auto'
  },
  tHeaderCol: {
    textAlign: 'center',
    // backgroundColor: '#f7f7f9',
    color: '#a6abbd',
    height: '4rem',
    fontSize: '12px',
    fontWeight: '400',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  tHeaderColStatic: {
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    height: '4rem',
    fontSize: '12px',
    fontWeight: '400'
  },
  tCol: {
    textAlign: 'center',
    color: theme.palette.background.dark,
    borderTop: '1px solid #cdd2d21A',
    fontSize: '12px'
  },
  teamDisplay: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  tBody: {
    marginTop: '-3px'
  },
  timeEntry: {
    display: 'flex',
    justifyContent: 'space-evenly'
  },
  banner: {
    width: '100%',
    background: 'none'
  },
  profileLink: {
    // color: theme.palette.background.dark,
    fontWeight: '700',
    '&:hover': {
      color: theme.palette.text.primary
    },
    fontSize: '12px'
  },
  videoLink: {
    color: theme.palette.secondary.main,
    '&:hover': {
      color: theme.palette.tertiary.secondary
    },
    cursor: 'pointer'
  },
  hidden: {
    display: 'none'
  },
  tooltip: {
    fontSize: '12px'
  },
  characterImage: {
    backgroundColor: '#ebeef1',
    boxShadow: '0px 0px 1px 1px #cdd2d2',
    border: '1px solid #cdd2d2',
    height: '35px',
    width: '35px',
    margin: '0.1rem'
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1rem',
    marginBottom: '1rem',
    color: theme.palette.text.primary
  },
  details: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  text: {
    fontSize: '12px',
    color: '#8c96a2'
  },
  breakdown: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    minWidth: '10rem'
  },
  ranking: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  highTier: {
    color: '#e29205 !important'
  },
  midTier: {
    color: '#1f8ecf !important'
  },
  lowTier: {
    color: '#2daf7f !important'
  },
  noTier: {
    color: '#737373 !important'
  },
  important: {
    fontWeight: '600',
    color: theme.palette.text.primary
  }
}));

function UserProfileAbyssRanking(props) {
  const classes = useStyles();

  const tableHeaders = [
    {
      label: 'Abyss Version',
      id: 'abyssVersion'
    },
    {
      label: 'Rank',
      id: 'rank'
    },
    {
      label: 'Points',
      id: 'points'
    },
    {
      label: 'Breakdown',
      id: 'breakdown'
    }
  ];

  const breakdownCategories = [
    '12-1-1',
    '12-1-2',
    '12-2-1',
    '12-2-2',
    '12-3-1',
    '12-3-2'
  ];

  const tableHeaderIds = map(tableHeaders, 'id');

  const detailTooltips = {
    breakdown: 'A more detailed breakdown of player score in each chamber'
  };

  return (
    <Box classes={{ root: classes.tableContainer }}>
      <Table className={classes.tableView} stickyHeader size="small">
        <TableHead className={classes.tableHeader}>
          <TableRow>
            {map(tableHeaders, header => {
              return (
                <TableCell
                  key={header.id}
                  id={header.id}
                  className={classes.tHeaderColStatic}
                >
                  {!!detailTooltips[header.id] && (
                    <Tooltip
                      classes={{ tooltip: classes.tooltip }}
                      title={detailTooltips[header.id]}
                    >
                      <Box classes={{ root: classes.details }}>
                        {header.label}
                        <Box mr={1} />
                        <HelpIcon style={{ color: '#ced3d3' }} />
                      </Box>
                    </Tooltip>
                  )}
                  {!detailTooltips[header.id] && header.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody className={classes.tBody}>
          {map(props.data, (entry, idx) => {
            return (
              <TableRow
                className={clsx({
                  [classes.root]: true,
                  [classes.oddRow]: idx % 2,
                  [classes.evenRow]: !(idx % 2)
                })}
                key={`${entry.rank._id}-${idx}`}
              >
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'abyssVersion'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'abyssVersion'
                    )
                  })}
                >
                  <Typography classes={{ root: classes.text }}>
                    {entry.version}
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(tableHeaderIds, h => h === 'rank'),
                    [classes.hidden]: !find(tableHeaderIds, h => h === 'rank')
                  })}
                >
                  <Box className={classes.ranking}>
                    <Typography
                      style={{ fontWeight: 'bold' }}
                      classes={{
                        root: clsx({
                          [classes.text]: true,
                          [classes.highTier]: get(entry.rank, 'rank') <= 5,
                          [classes.midTier]:
                            5 < get(entry.rank, 'rank') &&
                            get(entry.rank, 'rank') <= 15,
                          [classes.lowTier]:
                            15 < get(entry.rank, 'rank') &&
                            get(entry.rank, 'rank') <= 30,
                          [classes.noTier]: 30 < get(entry.rank, 'rank')
                        })
                      }}
                    >
                      {entry.rank.rank}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(tableHeaderIds, h => h === 'points'),
                    [classes.hidden]: !find(tableHeaderIds, h => h === 'points')
                  })}
                >
                  <Typography classes={{ root: classes.text }}>
                    {round(entry.rank.points, 2)}
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'breakdown'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'breakdown'
                    )
                  })}
                >
                  <Box className={classes.breakdown}>
                    {map(breakdownCategories, (key, idx) => {
                      return (
                        <Box
                          key={`breakdown-${idx}`}
                          style={{
                            display: 'flex',
                            width: `${100 / size(breakdownCategories)}%`,
                            flexWrap: 'nowrap'
                          }}
                        >
                          <Typography
                            style={{
                              width: 'fit-content',
                              marginRight: '0.5rem'
                            }}
                            classes={{ root: classes.text }}
                          >{`${key}: `}</Typography>
                          {get(entry.rank.breakdown, `${key}.rank`) && (
                            <Tooltip title="Click to watch video">
                              <Link
                                onClick={props.loadVideo(
                                  get(entry.rank.breakdown, `${key}`),
                                  get(entry, 'rank.alias')
                                )}
                                underline="hover"
                                target="_blank"
                              >
                                <Typography
                                  style={{
                                    width: 'fit-content'
                                  }}
                                  classes={{
                                    root: clsx({
                                      [classes.text]: true,
                                      [classes.videoLink]: true,
                                      [classes.important]:
                                        get(
                                          entry.breakdown,
                                          `${key}.rank`,
                                          99
                                        ) === 1,
                                      [classes.highTier]:
                                        get(
                                          entry.rank.breakdown,
                                          `${key}.rank`,
                                          99
                                        ) <= 5,
                                      [classes.midTier]:
                                        5 <
                                          get(
                                            entry.rank.breakdown,
                                            `${key}.rank`,
                                            99
                                          ) &&
                                        get(
                                          entry.rank.breakdown,
                                          `${key}.rank`,
                                          99
                                        ) <= 15,
                                      [classes.lowTier]:
                                        15 <
                                          get(
                                            entry.rank.breakdown,
                                            `${key}.rank`,
                                            99
                                          ) &&
                                        get(entry.rank, 'rank', 99) <= 30,
                                      [classes.noTier]:
                                        30 <
                                        get(
                                          entry.rank.breakdown,
                                          `${key}.rank`,
                                          99
                                        )
                                    })
                                  }}
                                  noWrap
                                >
                                  {get(entry.rank.breakdown, `${key}.rank`) ??
                                    '-'}
                                  {get(entry.rank.breakdown, `${key}.rank`) &&
                                    !includes(
                                      [1, 2, 3],
                                      get(entry.rank.breakdown, `${key}.rank`)
                                    ) &&
                                    'th'}
                                </Typography>
                              </Link>
                            </Tooltip>
                          )}
                          {!get(entry.rank.breakdown, `${key}.rank`) && '-'}
                          <Box style={{ marginRight: '2rem' }}></Box>
                        </Box>
                      );
                    })}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {isEmpty(props.data) && (
        <Box classes={{ root: classes.empty }}>
          <Typography variant="h4">No information to display.</Typography>
        </Box>
      )}
    </Box>
  );
}

UserProfileAbyssRanking.propTypes = {
  data: PropTypes.array.isRequired,
  classes: PropTypes.object,
  loadVideo: PropTypes.func.isRequired
};

export default UserProfileAbyssRanking;
